import { AppointmentProposal } from './appointmentProposals.models';

export type AppointmentProposalErrorKind = 'NOT_FOUND' | 'CREATE' | 'UPDATE';

export interface AppointmentProposalsState {
  ids: string[];
  entities: Record<string, AppointmentProposal>;
  selectedId: string | null;
  selectedAppointmentId: string | null;
  loadedAppointmentIds: string[];
  error: string | null;
  errorKind: AppointmentProposalErrorKind | null;
  isLoaded: boolean;
  isLoading: boolean;
}

type Action =
  | {
      type: 'FETCH_APPOINTMENT_PROPOSALS';
      payload: { appointmentId: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSALS_SUCCESS';
      payload: { appointmentProposals: AppointmentProposal[] };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSALS_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL';
      payload: { appointmentId: string; appointmentProposalId: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_SUCCESS';
      payload: { appointmentProposal: AppointmentProposal };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_FAILURE';
      payload: { error: string };
    }
  | { type: 'FETCH_APPOINTMENT_PROPOSAL_CANCELLED' }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL';
      payload: { appointmentId: string };
    }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_SUCCESS';
      payload: { appointmentProposal: AppointmentProposal };
    }
  | {
      type: 'CREATE_APPOINTMENT_PROPOSAL_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL';
      payload: {
        appointmentId: string;
        appointmentProposalId: string;
        appointmentProposal: Partial<AppointmentProposal>;
      };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_SUCCESS';
      payload: {
        appointmentProposal: AppointmentProposal;
      };
    }
  | {
      type: 'UPDATE_APPOINTMENT_PROPOSAL_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'SEND_PROPOSAL_FOLLOW_UP';
      payload: { appointmentId: string; appointmentProposalId: string };
    }
  | {
      type: 'SEND_PROPOSAL_FOLLOW_UP_SUCCESS';
      payload: { appointmentProposalId: string };
    }
  | {
      type: 'SEND_PROPOSAL_FOLLOW_UP_FAILURE';
      payload: { error: string };
    }
  | {
      type: 'UPSERT_PROPOSALS';
      payload: { appointmentProposals: AppointmentProposal[] };
    }
  | {
      type: 'SETTLE_PROPOSALS';
      payload: {
        appointmentProposals: AppointmentProposal[];
        error?: string;
        errorKind?: AppointmentProposalErrorKind;
      };
    }
  | {
      type: 'SET_SELECTED_APPOINTMENT';
      payload: { appointmentId: string };
    }
  | {
      type: 'SET_SELECTED_APPOINTMENT_PROPOSAL';
      payload: { appointmentId: string; appointmentProposalId: string };
    }
  | { type: 'SET_APPOINTMENT_PROPOSAL_MUTATING' };

export const initState: AppointmentProposalsState = {
  ids: [],
  entities: {},
  selectedId: null,
  selectedAppointmentId: null,
  loadedAppointmentIds: [],
  error: null,
  errorKind: null,
  isLoaded: false,
  isLoading: true,
};

export const appointmentProposalsReducer = (
  state: AppointmentProposalsState,
  action: Action
): AppointmentProposalsState => {
  switch (action.type) {
    case 'FETCH_APPOINTMENT_PROPOSALS':
      return {
        ...state,
        error: null,
        errorKind: null,
        isLoading: true,
        isLoaded: state.loadedAppointmentIds.includes(
          action.payload.appointmentId
        ),
        selectedAppointmentId: action.payload.appointmentId,
      };
    case 'FETCH_APPOINTMENT_PROPOSALS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        loadedAppointmentIds: [
          ...new Set([
            ...state.loadedAppointmentIds,
            ...action.payload.appointmentProposals.map((p) => p.appointmentId),
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.appointmentProposals.map(
              (a) => a.appointmentProposalId
            ),
          ]),
        ],
        entities: action.payload.appointmentProposals.reduce(
          (prev, curr) => ({ ...prev, [curr.appointmentProposalId]: curr }),
          state.entities
        ),
      };
    case 'CREATE_APPOINTMENT_PROPOSAL':
      return {
        ...state,
        error: null,
        errorKind: null,
        isLoading: true,
        isLoaded: false,
        selectedAppointmentId: action.payload.appointmentId,
      };
    case 'UPDATE_APPOINTMENT_PROPOSAL':
    case 'FETCH_APPOINTMENT_PROPOSAL':
    case 'SEND_PROPOSAL_FOLLOW_UP':
      return {
        ...state,
        error: null,
        errorKind: null,
        isLoading: true,
        isLoaded: state.ids.includes(action.payload.appointmentProposalId),
        selectedId: action.payload.appointmentProposalId,
        selectedAppointmentId: action.payload.appointmentId,
      };
    case 'CREATE_APPOINTMENT_PROPOSAL_SUCCESS':
    case 'FETCH_APPOINTMENT_PROPOSAL_SUCCESS':
    case 'UPDATE_APPOINTMENT_PROPOSAL_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        selectedId: action.payload.appointmentProposal.appointmentProposalId,
        selectedAppointmentId: action.payload.appointmentProposal.appointmentId,
        ids: [
          ...new Set([
            ...state.ids,
            action.payload.appointmentProposal.appointmentProposalId,
          ]),
        ],
        entities: {
          ...state.entities,
          [action.payload.appointmentProposal.appointmentProposalId]:
            action.payload.appointmentProposal,
        },
      };
    case 'SEND_PROPOSAL_FOLLOW_UP_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
      };
    case 'FETCH_APPOINTMENT_PROPOSALS_FAILURE':
    case 'FETCH_APPOINTMENT_PROPOSAL_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
        errorKind: 'NOT_FOUND',
      };

    case 'CREATE_APPOINTMENT_PROPOSAL_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
        errorKind: 'CREATE',
      };

    case 'UPDATE_APPOINTMENT_PROPOSAL_FAILURE':
    case 'SEND_PROPOSAL_FOLLOW_UP_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error,
        errorKind: 'UPDATE',
      };

    case 'FETCH_APPOINTMENT_PROPOSAL_CANCELLED':
      return {
        ...state,
        error: null,
        errorKind: null,
      };

    case 'SET_SELECTED_APPOINTMENT':
      return {
        ...state,
        isLoading: true,
        isLoaded: state.loadedAppointmentIds.includes(
          action.payload.appointmentId
        ),
        error: null,
        errorKind: null,
        selectedAppointmentId: action.payload.appointmentId,
      };

    case 'SET_SELECTED_APPOINTMENT_PROPOSAL':
      return {
        ...state,
        isLoading: true,
        isLoaded: state.ids.includes(action.payload.appointmentProposalId),
        error: null,
        errorKind: null,
        selectedAppointmentId: action.payload.appointmentId,
        selectedId: action.payload.appointmentProposalId,
      };

    case 'UPSERT_PROPOSALS': {
      const upsertedProposals = action.payload.appointmentProposals;
      return {
        ...state,
        loadedAppointmentIds: [
          ...new Set([
            ...state.loadedAppointmentIds,
            ...upsertedProposals.map((o) => o.appointmentId),
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...upsertedProposals.map((a) => a.appointmentProposalId),
          ]),
        ],
        entities: upsertedProposals.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalId]: curr,
          }),
          state.entities
        ),
      };
    }

    case 'SETTLE_PROPOSALS': {
      const settled = action.payload.appointmentProposals;
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: action.payload.error ?? null,
        errorKind: action.payload.errorKind ?? null,
        loadedAppointmentIds: [
          ...new Set([
            ...state.loadedAppointmentIds,
            ...(state.selectedAppointmentId
              ? [state.selectedAppointmentId]
              : []),
            ...settled.map((p) => p.appointmentId),
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...settled.map((p) => p.appointmentProposalId),
          ]),
        ],
        entities: settled.reduce(
          (prev, curr) => ({ ...prev, [curr.appointmentProposalId]: curr }),
          state.entities
        ),
      };
    }

    case 'SET_APPOINTMENT_PROPOSAL_MUTATING':
      return { ...state, isLoading: true, error: null, errorKind: null };

    default:
      return state;
  }
};
